import { useCallback } from 'react'

import { useApolloClient } from '@apollo/client/react'

import { useRefreshFeed } from './useRefreshFeed'
import { useRefreshFeedCollection } from './useRefreshFeedCollection'

type RefreshAllFeedsArgs = {
  organizationId: string
  feedIds: string[]
  collectionIds: string[]
}

type RefreshAllFeedsResult = {
  success: boolean
}

/**
 * Refreshes every root feed and feed collection for the All Feeds view.
 *
 * Fans out the `refreshFeed` / `refreshFeedCollection` mutations in parallel,
 * then refetches the feed items and sidebar feeds once after all refreshes
 * settle, so the view reflects the new `lastRefreshed` timestamps without
 * triggering one refetch per mutation.
 */
export const useRefreshAllFeeds = (): ((
  args: RefreshAllFeedsArgs,
) => Promise<RefreshAllFeedsResult>) => {
  const client = useApolloClient()
  const [refreshFeed] = useRefreshFeed()
  const [refreshFeedCollection] = useRefreshFeedCollection()

  return useCallback(
    async ({ organizationId, feedIds, collectionIds }) => {
      const results = await Promise.all([
        ...feedIds.map((feedId) =>
          refreshFeed({
            variables: { input: { feedId, organizationId } },
            // suppress the per-mutation refetches; a single refetch
            // happens below once every refresh has settled
            refetchQueries: [],
          }),
        ),
        ...collectionIds.map((feedCollectionId) =>
          refreshFeedCollection({
            variables: { input: { feedCollectionId, organizationId } },
            refetchQueries: [],
          }),
        ),
      ])

      await client.refetchQueries({
        include: ['GetFeedItems', 'GetFeedsForSidebar'],
      })

      return { success: results.every((result) => result.success) }
    },
    [client, refreshFeed, refreshFeedCollection],
  )
}
